import { useEffect, useRef, useState } from "react";
import type { ResolveResponse } from "../lib/types.ts";

export type PlayerStatus = "loading" | "live" | "cached" | "fallback";
export type DisplayCommand = "reload" | "refresh" | "blank" | "unblank";

const cacheKey = (id: string) => `lbc-plan-${id}`;
const MIN_POLL_SECONDS = 5;
const ERROR_RETRY_SECONDS = 15;
const REALTIME_POLL_MS = 4000;
// Re-apply a plan (to pick up freshly-signed image URLs) once the currently
// applied URLs get within this window of expiry. Image URLs are short-lived
// Blob SAS links; the resolve response rotates them every call, but the player
// otherwise keeps the same plan object while the content revision is unchanged —
// so without this the on-screen <img> URLs eventually expire and every slide
// 403s to a black screen. See planExpiresAt().
const SAS_REFRESH_SAFETY_MS = 20 * 60 * 1000;

/** Earliest Blob-SAS expiry (`se=`) across a plan's item URLs, or null if none. */
function planExpiresAt(plan: ResolveResponse | null): number | null {
  if (!plan) return null;
  let min = Infinity;
  for (const it of plan.items ?? []) {
    const m = it.url ? /[?&]se=([^&]+)/.exec(it.url) : null;
    if (m) {
      const t = Date.parse(decodeURIComponent(m[1]));
      if (!Number.isNaN(t)) min = Math.min(min, t);
    }
  }
  return min === Infinity ? null : min;
}

function loadCached(id: string): ResolveResponse | null {
  try {
    const raw = localStorage.getItem(cacheKey(id));
    return raw ? (JSON.parse(raw) as ResolveResponse) : null;
  } catch {
    return null;
  }
}

function saveCached(id: string, plan: ResolveResponse): void {
  try {
    localStorage.setItem(cacheKey(id), JSON.stringify(plan));
  } catch {
    /* storage full / unavailable — non-fatal */
  }
}

/** Warm the browser/SW cache with the plan's media so it survives an outage. */
function precache(urls: string[]): void {
  for (const u of urls) {
    fetch(u, { mode: "cors" }).catch(() => {});
  }
}

/**
 * Drives the player: polls /api/resolve every `revalidateAfterSeconds`, swaps the
 * plan only when `revision` changes, sends a heartbeat each cycle, and persists
 * the last good plan to localStorage so reloads/outages still show content.
 * Never throws — a failure keeps the last plan (status "cached") or, if there is
 * none, signals "fallback" so the player shows its built-in slide.
 */
export function usePlayerPlan(
  displayId: string,
  token: string | null,
  onCommand?: (action: DisplayCommand) => void,
  /** Dashboard preview: render the plan but send NO heartbeat, so the admin's
   *  browser never masquerades as the physical screen in health data. */
  preview = false,
): { plan: ResolveResponse | null; status: PlayerStatus } {
  const [plan, setPlan] = useState<ResolveResponse | null>(() =>
    loadCached(displayId),
  );
  const [status, setStatus] = useState<PlayerStatus>(() =>
    loadCached(displayId) ? "cached" : "loading",
  );
  const revisionRef = useRef<string | undefined>(plan?.revision);
  // Expiry of the currently-applied image URLs (0 ⇒ apply fresh on first tick,
  // which also discards any expired URLs rehydrated from localStorage).
  const planExpiryRef = useRef(0);
  // Keep the latest callback without re-subscribing the polling loops.
  const onCommandRef = useRef(onCommand);
  onCommandRef.current = onCommand;

  useEffect(() => {
    if (!token) {
      setStatus("fallback");
      return;
    }
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let realtimeTimer: ReturnType<typeof setTimeout>;
    const q = `display=${encodeURIComponent(displayId)}&token=${encodeURIComponent(token)}`;

    const schedule = (secs: number) => {
      timer = setTimeout(tick, Math.max(MIN_POLL_SECONDS, secs) * 1000);
    };

    // ---- Realtime: short-poll the event bus for fast updates + commands ----
    let cursor: string | null = null;
    async function realtimeTick(): Promise<void> {
      try {
        const url =
          cursor == null
            ? `/api/realtime/poll?${q}`
            : `/api/realtime/poll?${q}&after=${encodeURIComponent(cursor)}`;
        const res = await fetch(url, { cache: "no-store" });
        if (res.ok) {
          const data = (await res.json()) as {
            cursor: string;
            events: Array<{ type: string; payload?: { action?: DisplayCommand } }>;
          };
          cursor = data.cursor;
          for (const ev of data.events) {
            if (ev.type === "content-updated" || ev.type === "override") {
              void tick(); // pull the new plan immediately
            } else if (ev.type === "command") {
              const action = ev.payload?.action;
              if (action === "refresh") void tick();
              if (action) onCommandRef.current?.(action);
            }
          }
        }
      } catch {
        /* transient — try again next interval */
      }
      if (!stopped) realtimeTimer = setTimeout(realtimeTick, REALTIME_POLL_MS);
    }

    async function tick(): Promise<void> {
      try {
        const res = await fetch(`/api/resolve?${q}`, { cache: "no-store" });
        if (!res.ok) throw new Error(`resolve ${res.status}`);
        const data = (await res.json()) as ResolveResponse;
        if (stopped) return;

        // Apply the plan when the content changed, OR when the on-screen image
        // URLs are about to expire (so their SAS can't lapse into a black 403),
        // OR on the first successful tick (planExpiryRef starts at 0) — which
        // also replaces any expired URLs restored from localStorage.
        const contentChanged = data.revision !== revisionRef.current;
        const urlsNearExpiry =
          Date.now() > planExpiryRef.current - SAS_REFRESH_SAFETY_MS;
        if (contentChanged || urlsNearExpiry) {
          revisionRef.current = data.revision;
          setPlan(data);
          saveCached(displayId, data);
          planExpiryRef.current = planExpiresAt(data) ?? Number.POSITIVE_INFINITY;
          if (contentChanged) precache(data.cacheManifest ?? []);
        }
        const nextStatus = data.mode === "fallback" ? "fallback" : "live";
        setStatus(nextStatus);

        // Liveness ping + telemetry (fire-and-forget): what the screen is
        // showing right now, so the fleet dashboard can flag stuck/fallback TVs.
        // Skipped in preview mode so the dashboard iframe doesn't pollute health.
        if (!preview)
        fetch(`/api/heartbeat?${q}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status: nextStatus,
            mode: data.mode,
            revision: data.revision,
            itemCount: data.items?.length ?? 0,
            ua: navigator.userAgent,
            w: window.screen?.width,
            h: window.screen?.height,
          }),
        }).catch(() => {});

        schedule(data.revalidateAfterSeconds ?? 30);
      } catch {
        if (stopped) return;
        // Keep showing the last good plan; only fall back if we never had one.
        setStatus(revisionRef.current ? "cached" : "fallback");
        schedule(ERROR_RETRY_SECONDS);
      }
    }

    void tick();
    void realtimeTick();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(realtimeTimer);
    };
  }, [displayId, token]);

  return { plan, status };
}
